/**
 * `#uc2Scaling` — "Scaling opportunity areas" (client round 3 October 2026), above the
 * pairs on the Similar watersheds tab.
 *
 * The pairs block says "these two places are alike and one of them is doing better". This
 * one answers the question a programme officer asks next: **where would we take a practice
 * first?** One ranked row per candidate, each carrying the four things that decide it —
 * which watershed with positive change it would learn from and how alike the two are, the
 * practice it is furthest behind on and by how many points, whether the rainfall of the
 * two is comparable, and the run's own 0–1 score with its verdict in a word.
 *
 * **Nothing here is scored on the client.** `score` and `verdict` are the engine's, read
 * as served; the pane ranks on the score and colours the badge from the verdict. A build
 * that re-derived either would disagree with the engine the day the definition moves, and
 * a verdict is exactly the kind of statement two sources must never give two answers for.
 *
 * Which rows: the scope's own list, which is the programme at watershed level and one
 * country at country level. With a unit focused the list narrows to that unit alone and
 * gains one plain sentence saying, from the same numbers, why it came out where it did.
 *
 * A run that carries no `scaling_candidates` yet prints one short note.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUc2ScalingRow } from '../../lib/api';
import { fmtFixed } from '../../lib/format';
import {
  UC2_PROGRAMME_SCOPE,
  UC2_SCOPE_ISO3,
  UC2_VERDICT_COLORS,
  clampScore,
  normaliseVerdict,
  rankScaling,
  unitInScope,
} from './uc2Constants';

export interface Uc2ScalingProps {
  /** `summary.scaling_candidates`, whole — the scope's own slice is taken here. */
  candidates?: Record<string, IntelUc2ScalingRow[]> | undefined;
  /** `'programme'`, or the admin0 code the country level is reading. */
  scope: string;
  /** The focused unit, or `null`: one row and one sentence instead of the list. */
  focusedId?: string | null;
  /** A practice group's own label, so the row names the group the way the selector does. */
  practiceLabel: (group: string) => string;
  /** How many rows at most. */
  limit?: number;
}

/** A candidate belongs to a country scope when it says so, or when its id starts with it. */
function rowInScope(row: IntelUc2ScalingRow, scope: string): boolean {
  if (scope === UC2_PROGRAMME_SCOPE || !scope) return true;
  if (row.country) return row.country === scope;
  return unitInScope(row.unit_id, scope);
}

/** `similarity` as whole percent, clamped — the same reading the pair cards print. */
function alikePct(similarity: number | null | undefined): number {
  if (typeof similarity !== 'number' || !Number.isFinite(similarity)) return 0;
  return Math.round(Math.min(1, Math.max(0, similarity)) * 100);
}

/** The gap in whole points, or `null` when the run recorded none. */
function gapPoints(gap: number | null | undefined): number | null {
  if (typeof gap !== 'number' || !Number.isFinite(gap)) return null;
  return Math.abs(Math.round(gap));
}

export function Uc2Scaling({
  candidates,
  scope,
  focusedId = null,
  practiceLabel,
  limit = 10,
}: Uc2ScalingProps) {
  const { t } = useTranslation();
  /**
   * **A served empty list is not a missing block.** "This run carries nothing yet" and
   * "this scope has nothing left to scale" are different facts, and printing the first
   * for the second tells a reader their run is broken when it is in fact finished. So the
   * two are told apart by whether the run served a list at all.
   */
  const served =
    Boolean(candidates) &&
    (candidates![scope] != null || candidates![UC2_PROGRAMME_SCOPE] != null);
  const all = (candidates?.[scope] ?? candidates?.[UC2_PROGRAMME_SCOPE] ?? []).filter((row) =>
    rowInScope(row, scope),
  );
  const ranked = rankScaling(all);
  const focusedRow = focusedId ? ranked.find((row) => row.unit_id === focusedId) : undefined;
  // With a unit focused the block is about that unit. A focused unit the run has no
  // candidate row for falls back to the list rather than printing an empty frame: "we
  // have no candidate for this place" is said by the place simply not being in the list.
  const rows = focusedRow ? [focusedRow] : ranked.slice(0, limit);

  /** Which of the three rainfall words a row takes. */
  const rainKey = (comparable: boolean | null | undefined) =>
    comparable === true ? 'rainYes' : comparable === false ? 'rainNo' : 'rainUnknown';
  const rainClauseKey = (comparable: boolean | null | undefined) =>
    comparable === true ? 'yes' : comparable === false ? 'no' : 'unknown';

  /** The focused row's own sentence, built from the very numbers the row prints. */
  const focusSentence = (row: IntelUc2ScalingRow): string => {
    const verdict = normaliseVerdict(row.verdict);
    const score = clampScore(row.score);
    const points = gapPoints(row.best_gap);
    const rain = t(`intel.uc2.scaling.rainClause.${rainClauseKey(row.rain_comparable)}`);
    /**
     * Three cases, not two. A run can serve a reference and **no** practice gap — every
     * group's gap negative or null, so the engine leaves `best_group` null — and folding
     * that into the no-reference wording made the sentence deny a reference the row right
     * above it had just named. So the missing gap drops only the gap clause.
     */
    const detail = !row.learn_from
      ? t('intel.uc2.scaling.focus.detailNoLearn', { rain })
      : points != null && row.best_group
        ? t('intel.uc2.scaling.focus.detail', {
            pct: alikePct(row.learn_from.similarity),
            learn: row.learn_from.name,
            points,
            count: points,
            practice: practiceLabel(row.best_group),
            rain,
          })
        : t('intel.uc2.scaling.focus.detailNoGap', {
            pct: alikePct(row.learn_from.similarity),
            learn: row.learn_from.name,
            rain,
          });
    return t(`intel.uc2.scaling.focus.${verdict}`, {
      name: row.name,
      score: score != null ? fmtFixed(score, 2) : '—',
      detail,
    });
  };

  return (
    <div id="uc2Scaling" className="result-box" data-testid="uc2-scaling">
      <h5>{t('intel.uc2.scaling.title')}</h5>
      <div className="result-pad">
        {rows.length ? (
          <>
            <ol className="uc2-scaling">
              {rows.map((row, i) => {
                const verdict = normaliseVerdict(row.verdict);
                const score = clampScore(row.score);
                const points = gapPoints(row.best_gap);
                return (
                  <li
                    key={row.unit_id}
                    className="uc2-scaling-row"
                    data-testid={`uc2-scaling-row-${row.unit_id}`}
                  >
                    <span className="uc2-scaling-rank" data-testid="uc2-scaling-rank">
                      {focusedRow ? ranked.indexOf(row) + 1 : i + 1}
                    </span>
                    <div className="uc2-scaling-main">
                      <p className="uc2-scaling-name">
                        {row.name}
                        {row.country ? (
                          <span className="uc2-scaling-country">
                            {t(`country.${UC2_SCOPE_ISO3[row.country] ?? row.country}`, {
                              defaultValue: row.country,
                            })}
                          </span>
                        ) : null}
                      </p>
                      <p className="sub" data-testid="uc2-scaling-learn">
                        {row.learn_from
                          ? t('intel.uc2.scaling.learnFrom', {
                              name: row.learn_from.name,
                              pct: alikePct(row.learn_from.similarity),
                            })
                          : t('intel.uc2.scaling.learnFromNone')}
                      </p>
                      <p className="sub" data-testid="uc2-scaling-gap">
                        {points != null && row.best_group
                          ? t('intel.uc2.scaling.gap', {
                              practice: practiceLabel(row.best_group),
                              points,
                              count: points,
                            })
                          : t('intel.uc2.scaling.gapNone')}
                      </p>
                      <p className="sub" data-testid="uc2-scaling-rain">
                        {t(`intel.uc2.scaling.${rainKey(row.rain_comparable)}`)}
                      </p>
                      <div className="uc2-scaling-score" data-testid="uc2-scaling-score">
                        <span
                          className="uc2-scaling-track"
                          role="img"
                          aria-label={t('intel.uc2.scaling.score', {
                            value: score != null ? fmtFixed(score, 2) : '—',
                          })}
                        >
                          <span style={{ width: `${(score ?? 0) * 100}%` }} />
                        </span>
                        <span className="uc2-scaling-num">
                          {score != null ? fmtFixed(score, 2) : '—'}
                        </span>
                      </div>
                    </div>
                    <span
                      className="uc2-verdict"
                      data-verdict={verdict}
                      style={{ color: UC2_VERDICT_COLORS[verdict] }}
                    >
                      {t(`intel.uc2.scaling.verdict.${verdict}`)}
                    </span>
                  </li>
                );
              })}
            </ol>
            {focusedRow ? (
              <p className="uc2-scaling-say" data-testid="uc2-scaling-focus">
                {focusSentence(focusedRow)}
              </p>
            ) : null}
            {!focusedRow && ranked.length > rows.length ? (
              <p className="sub" data-testid="uc2-scaling-truncated">
                {t('intel.uc2.scaling.truncated', { shown: rows.length, total: ranked.length })}
              </p>
            ) : null}
            <p className="sub" data-testid="uc2-scaling-how">
              {t('intel.uc2.scaling.how')}
            </p>
          </>
        ) : (
          <p
            className="note"
            data-testid={served ? 'uc2-scaling-none' : 'uc2-scaling-empty'}
          >
            {t(served ? 'intel.uc2.scaling.none' : 'intel.uc2.scaling.pending')}
          </p>
        )}
      </div>
    </div>
  );
}

export default Uc2Scaling;
